/**
 * CUSTOMER-172B — native micro-root search input.
 * React does NOT own keystrokes. No parent React updates on input/beforeinput frame.
 * Mount count must stay stable across typing / suggest updates.
 */
import {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
} from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  isSearchPerfDiagEnabled,
  startFocusInputTrace,
  traceFirstCharPaint,
  traceFocusInput,
} from "./search-focus-trace-v2.js";

export type SearchNativeInputHandlers = {
  /** Called AFTER paint / microtask — never sync on input frame. */
  onDraftAfterPaint: (value: string) => void;
  onSubmit: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onEscape?: () => void;
};

type Props = {
  lang: StoreV2Lang;
  /** Clear DOM value when this changes (new session). Does NOT remount. */
  sessionEpoch: number;
  handlersRef: MutableRefObject<SearchNativeInputHandlers>;
  /** Diagnostics: increments once per component instance. */
  mountCountRef?: MutableRefObject<number>;
  inputRef?: MutableRefObject<HTMLInputElement | null>;
};

function SearchNativeInputIslandInner({
  lang,
  sessionEpoch,
  handlersRef,
  mountCountRef,
  inputRef: externalInputRef,
}: Props) {
  const localRef = useRef<HTMLInputElement | null>(null);
  const inputEl = externalInputRef ?? localRef;
  const afterPaintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const composingRef = useRef(false);
  const lastValueRef = useRef("");

  useLayoutEffect(() => {
    if (mountCountRef) mountCountRef.current += 1;
  }, [mountCountRef]);

  // Session reset — clear DOM only, never remount via key=.
  useEffect(() => {
    if (sessionEpoch <= 0) return;
    const el = inputEl.current;
    if (el) el.value = "";
    lastValueRef.current = "";
  }, [sessionEpoch, inputEl]);

  // Native listeners — avoid React synthetic event path on hot keystrokes.
  useEffect(() => {
    const el = inputEl.current;
    if (!el) return;

    const scheduleAfterPaint = (value: string) => {
      if (afterPaintTimer.current) clearTimeout(afterPaintTimer.current);
      // Double-rAF ≈ after paint, then notify coordinator (debounce lives there).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (composingRef.current) return;
          handlersRef.current.onDraftAfterPaint(value);
        });
      });
    };

    const onPointerDown = () => {
      if (isSearchPerfDiagEnabled()) startFocusInputTrace();
      traceFocusInput("pointerdown");
    };

    const onFocus = () => {
      if (isSearchPerfDiagEnabled()) startFocusInputTrace();
      traceFocusInput("focus");
      handlersRef.current.onFocus?.();
    };

    const onBlur = () => {
      traceFocusInput("blur");
      handlersRef.current.onBlur?.();
    };

    const onBeforeInput = () => {
      traceFocusInput("beforeinput");
    };

    const onKeyDown = (e: KeyboardEvent) => {
      traceFocusInput("keydown");
      if (e.isComposing || composingRef.current) return;
      if (e.key === "Enter") {
        e.preventDefault();
        handlersRef.current.onSubmit(el.value);
        return;
      }
      if (e.key === "Escape") {
        handlersRef.current.onEscape?.();
      }
    };

    const onInput = () => {
      const t0 = performance.now();
      const v = el.value;
      lastValueRef.current = v;
      // O(1) bookkeeping only — NO React updates, NO tokenize, NO network.
      traceFocusInput("input", { value_len: v.length });
      if (!composingRef.current) {
        requestAnimationFrame(() => {
          traceFirstCharPaint();
          traceFocusInput("input_raf", { dt: performance.now() - t0 });
        });
        scheduleAfterPaint(v);
      }
    };

    const onCompositionStart = () => {
      composingRef.current = true;
      traceFocusInput("compositionstart");
    };

    const onCompositionEnd = () => {
      composingRef.current = false;
      traceFocusInput("compositionend");
      const v = el.value;
      lastValueRef.current = v;
      scheduleAfterPaint(v);
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("focus", onFocus);
    el.addEventListener("blur", onBlur);
    el.addEventListener("beforeinput", onBeforeInput);
    el.addEventListener("keydown", onKeyDown);
    el.addEventListener("input", onInput);
    el.addEventListener("compositionstart", onCompositionStart);
    el.addEventListener("compositionend", onCompositionEnd);

    return () => {
      if (afterPaintTimer.current) clearTimeout(afterPaintTimer.current);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("focus", onFocus);
      el.removeEventListener("blur", onBlur);
      el.removeEventListener("beforeinput", onBeforeInput);
      el.removeEventListener("keydown", onKeyDown);
      el.removeEventListener("input", onInput);
      el.removeEventListener("compositionstart", onCompositionStart);
      el.removeEventListener("compositionend", onCompositionEnd);
    };
  }, [handlersRef, inputEl]);

  // visualViewport: observe only for diagnostics — never React updates.
  useEffect(() => {
    if (!isSearchPerfDiagEnabled()) return;
    const vv = window.visualViewport;
    if (!vv) return;
    let lastH = vv.height;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    const onResize = () => {
      const h = vv.height;
      traceFocusInput("vv_resize", { h, dh: Math.abs(h - lastH) });
      lastH = h;
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        traceFocusInput("vv_settle", { h: vv.height });
      }, 120);
    };
    vv.addEventListener("resize", onResize);
    return () => {
      if (settleTimer) clearTimeout(settleTimer);
      vv.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <input
      ref={inputEl}
      type="search"
      inputMode="search"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="none"
      spellCheck={false}
      enterKeyHint="search"
      style={{ textTransform: "none" }}
      defaultValue=""
      data-search-native-island="1"
      data-testid="sv2-search-native-input"
      placeholder={
        lang === "en"
          ? "Search: tom yum, ramen, sauce, brand or SKU…"
          : "Пошук: том ям, рамен, соус, бренд або SKU…"
      }
      aria-autocomplete="list"
      aria-expanded={false}
    />
  );
}

export const SearchNativeInputIsland = memo(SearchNativeInputIslandInner);
